/**
 * Pure rules for how a support ticket reads and what can be done with it —
 * shared by the ticket list and the ticket detail view. No React, no store:
 * everything is derived from the ticket and who is looking at it.
 *
 * Follows the RFC, including its corrections to the mockup:
 *   - Resolve shows under the same condition as Unclaim: open, claimed, and
 *     either claimed by the viewer or the viewer has full support access
 *     (`chat.support.all`) — the server refuses anyone else with a 403;
 *   - a closed ticket that gets a new user message reopens UNASSIGNED (the
 *     assignment is cleared), not "with the same supporter".
 */

/** The fields these rules need; an inbox row satisfies it. */
export interface TicketLike {
  status: "open" | "closed";
  assigned_supporter_id: string | null;
  /** Display name for `assigned_supporter_id`; null/"" when unknown. */
  assigned_supporter_name: string | null;
}

export type TicketTagKind = "closed" | "unclaimed" | "mine" | "claimed";

export interface TicketActions {
  claim: boolean;
  unclaim: boolean;
  resolve: boolean;
}

const OTHER_SUPPORTER = "another supporter";

function isMine(t: TicketLike, viewerSub: string): boolean {
  return !!viewerSub && t.assigned_supporter_id === viewerSub;
}

function supporterName(t: TicketLike): string {
  return t.assigned_supporter_name || OTHER_SUPPORTER;
}

/** Which of the four states the ticket is in, from the viewer's side. */
export function ticketKind(t: TicketLike, viewerSub: string): TicketTagKind {
  if (t.status === "closed") return "closed";
  if (t.assigned_supporter_id === null) return "unclaimed";
  return isMine(t, viewerSub) ? "mine" : "claimed";
}

/** The list tag: "Closed" | "Unclaimed" | "Mine" | "Claimed · <name>". */
export function ticketTag(t: TicketLike, viewerSub: string): { kind: TicketTagKind; label: string } {
  const kind = ticketKind(t, viewerSub);
  const label =
    kind === "closed"
      ? "Closed"
      : kind === "unclaimed"
        ? "Unclaimed"
        : kind === "mine"
          ? "Mine"
          : `Claimed · ${supporterName(t)}`;
  return { kind, label };
}

/** The detail header's status line. */
export function ticketStatusText(t: TicketLike, viewerSub: string): string {
  switch (ticketKind(t, viewerSub)) {
    case "closed":
      return "Closed";
    case "unclaimed":
      return "Waiting for a supporter";
    case "mine":
      return "Assigned to you";
    case "claimed":
      return `Assigned to ${supporterName(t)}`;
  }
}

/** Claim when open and unclaimed; Unclaim and Resolve when open, claimed,
 *  and either the viewer's own ticket or the viewer has `chat.support.all`
 *  (`fullAccess`, the override); nothing when closed. */
export function ticketActions(t: TicketLike, viewerSub: string, fullAccess: boolean): TicketActions {
  const open = t.status === "open";
  const claimed = t.assigned_supporter_id !== null;
  const mayAct = open && claimed && (fullAccess || isMine(t, viewerSub));
  return {
    claim: open && !claimed,
    unclaim: mayAct,
    resolve: mayAct,
  };
}

/** Replies only on an open ticket assigned to the viewer specifically. */
export function canReply(t: TicketLike, viewerSub: string): boolean {
  return t.status === "open" && isMine(t, viewerSub);
}

/** Why the composer is hidden, or null when the viewer can reply. Only a
 *  viewer with `fullAccess` is told they can unclaim someone else's ticket. */
export function composerHint(t: TicketLike, viewerSub: string, fullAccess: boolean): string | null {
  switch (ticketKind(t, viewerSub)) {
    case "mine":
      return null;
    case "unclaimed":
      return "Claim this ticket to reply.";
    case "claimed":
      return fullAccess
        ? `Claimed by ${supporterName(t)} — you can unclaim it to return it to the queue.`
        : `Claimed by ${supporterName(t)}.`;
    case "closed":
      return "Ticket closed. If the user writes again it reopens unassigned, back in the Unclaimed queue.";
  }
}
